import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import {
  BadgeCategory,
  BadgeCategoryCreateRequest,
  BadgeCategoryDeleteResponse,
  BadgeCategoryEnableDisableResponse,
  BadgeCategoryListResponse,
  BadgeCategoryResponse,
  BadgeCategoryUpdateRequest,
} from '../models/badge-category.model';

@Injectable({
  providedIn: 'root',
})
export class BadgeCategoryService {
  private _allCategories: BehaviorSubject<BadgeCategoryListResponse | null> = new BehaviorSubject(null);
  private _category: BehaviorSubject<BadgeCategory | null> = new BehaviorSubject(null);

  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get allCategories$(): Observable<BadgeCategoryListResponse> {
    return this._allCategories.asObservable();
  }

  get category$(): Observable<BadgeCategory> {
    return this._category.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all badge categories
   */
  getAll(): Observable<BadgeCategoryListResponse> {
    return this._httpClient
      .get<BadgeCategoryListResponse>(environment.apiURL + 'badgeCategories/all')
      .pipe(tap((response) => this._allCategories.next(response)));
  }

  /**
   * Get badge category by ID
   */
  getById(id: string): Observable<BadgeCategoryResponse> {
    return this._httpClient
      .get<BadgeCategoryResponse>(environment.apiURL + `badgeCategories/${id}`)
      .pipe(tap((response) => this._category.next(response.category)));
  }

  /**
   * Create badge category
   */
  create(data: BadgeCategoryCreateRequest): Observable<BadgeCategoryResponse> {
    return this.allCategories$.pipe(
      take(1),
      switchMap((categories) =>
        this._httpClient.post<BadgeCategoryResponse>(environment.apiURL + 'badgeCategories/add', data).pipe(
          map((response) => {
            if (categories && categories.categories) {
              categories.categories.unshift(response.category);
              this._allCategories.next(categories);
            }
            return response;
          })
        )
      )
    );
  }

  /**
   * Update badge category
   */
  update(id: string, data: BadgeCategoryUpdateRequest): Observable<BadgeCategoryResponse> {
    return this.allCategories$.pipe(
      take(1),
      switchMap((categories) =>
        this._httpClient.put<BadgeCategoryResponse>(environment.apiURL + `badgeCategories/edit/${id}`, data).pipe(
          map((response) => {
            if (categories && categories.categories) {
              const index = categories.categories.findIndex((cat) => cat._id === id);
              if (index !== -1) {
                categories.categories[index] = response.category;
                this._allCategories.next(categories);
              }
            }
            return response;
          })
        )
      )
    );
  }

  /**
   * Enable/Disable badge category
   */
  enableDisable(id: string): Observable<BadgeCategoryEnableDisableResponse> {
    return this.allCategories$.pipe(
      take(1),
      switchMap((categories) =>
        this._httpClient
          .get<BadgeCategoryEnableDisableResponse>(environment.apiURL + `badgeCategories/enableDisable/${id}`)
          .pipe(
            map((response) => {
              if (categories && categories.categories) {
                const index = categories.categories.findIndex((cat) => cat._id === id);
                if (index !== -1) {
                  categories.categories[index].isDisabled = response.isDisabled;
                  this._allCategories.next(categories);
                }
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Delete badge category
   */
  delete(id: string): Observable<BadgeCategoryDeleteResponse> {
    return this.allCategories$.pipe(
      take(1),
      switchMap((categories) =>
        this._httpClient.delete<BadgeCategoryDeleteResponse>(environment.apiURL + `badgeCategories/delete/${id}`).pipe(
          map((response) => {
            if (categories && categories.categories) {
              const index = categories.categories.findIndex((cat) => cat._id === id);
              if (index !== -1) {
                categories.categories.splice(index, 1);
                this._allCategories.next(categories);
              }
            }
            return response;
          })
        )
      )
    );
  }
}
